import { TRPCClientError } from "@trpc/client";

// tRPC errors with these codes carry messages authored by our own error
// factories (newBadRequestError, newForbiddenError, etc.) and are safe to
// render. Anything else — unhandled throws, DB errors, Kysely stack text —
// is suppressed behind a generic fallback so we don't leak server internals
// to the UI. Keep in sync with factories in apps/api/src/utils/errors.ts.
const SAFE_TRPC_ERROR_CODES: ReadonlySet<string> = new Set([
  "BAD_REQUEST",
  "FORBIDDEN",
  "NOT_FOUND",
  "UNAUTHORIZED",
  "PRECONDITION_FAILED",
  "TIMEOUT",
  "TOO_MANY_REQUESTS",
]);

/**
 * The user-facing message a tRPC query/mutation error carries, or `undefined`
 * when it carries none that is safe to render.
 *
 * - Returns the server message only when the error is a `TRPCClientError` with
 *   a whitelisted `code` (authored by our error factories).
 * - Returns `undefined` for plain JS `Error` instances (network,
 *   serialization), non-whitelisted tRPC codes (e.g. INTERNAL_SERVER_ERROR),
 *   and non-Error values.
 *
 * Where some text has to render, use `describeQueryError` with a fallback. An
 * error toast's title already says what failed, so `showMutationError` leaves
 * the description out instead.
 */
export function describeSafeErrorMessage(error: unknown): string | undefined {
  if (error instanceof TRPCClientError) {
    const code = getTrpcErrorCode(error);
    if (code !== null && SAFE_TRPC_ERROR_CODES.has(code)) {
      return error.message;
    }
  }
  return undefined;
}

/**
 * Safely extract a user-facing error message from a tRPC query/mutation error:
 * the message `describeSafeErrorMessage` allows, or `fallback` when it allows
 * none.
 */
export function describeQueryError(error: unknown, fallback: string): string {
  return describeSafeErrorMessage(error) ?? fallback;
}

/**
 * Whether a query/mutation failure carries a specific tRPC error code, for a caller that must
 * react differently to different rejections — e.g. re-reading a list when the server says the
 * caller's view of a row is out of date, rather than when the input itself was wrong.
 */
export function isTrpcErrorCode(error: unknown, code: string): boolean {
  return getTrpcErrorCode(error) === code;
}

// A read refused for good: the thing is gone, or the caller (or the link) may no longer see it.
// Asking again gives the same answer, unlike a dropped connection or a server error.
const REFUSED_READ_TRPC_ERROR_CODES: ReadonlySet<string> = new Set([
  "NOT_FOUND",
  "FORBIDDEN",
  "UNAUTHORIZED",
  "PRECONDITION_FAILED",
]);

/**
 * Whether a failed read is the server refusing it for good rather than a passing failure — what
 * decides whether a page that already shows the data may keep it after a failed refetch.
 */
export function isRefusedRead(error: unknown): boolean {
  const code = getTrpcErrorCode(error);
  return code !== null && REFUSED_READ_TRPC_ERROR_CODES.has(code);
}

// A request the server turned down for what it asked, not for how it travelled: sent again unchanged
// it gets the same answer. A dropped connection, a timeout, a 429 or a 5xx may pass on a second try.
const REJECTED_REQUEST_TRPC_ERROR_CODES: ReadonlySet<string> = new Set([
  "BAD_REQUEST",
  "PARSE_ERROR",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "METHOD_NOT_SUPPORTED",
  "CONFLICT",
  "PRECONDITION_FAILED",
  "PRECONDITION_REQUIRED",
  "PAYLOAD_TOO_LARGE",
  "UNSUPPORTED_MEDIA_TYPE",
  "UNPROCESSABLE_CONTENT",
]);

// The 4xx statuses that are about timing, not about the request: a timeout, a request sent too early,
// a rate limit. Asked again later they can pass.
const TRANSIENT_CLIENT_ERROR_STATUSES: ReadonlySet<number> = new Set([408, 425, 429]);

// TanStack's own default (`retry: 3`), which a `retry` function replaces.
const MAX_QUERY_RETRIES = 3;

/**
 * The default `retry` of every query: TanStack's three attempts for a failure that can pass, none for
 * a request the server refused. The stock default repeats a 403 or a 404 three times, so each refused
 * read costs four requests, four server error logs and several seconds of spinner before the page can
 * say no. A query with recovery of its own still sets `retry: false`.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isRejectedRequest(error)) return false;
  return failureCount < MAX_QUERY_RETRIES;
}

function isRejectedRequest(error: unknown): boolean {
  const code = getTrpcErrorCode(error);
  if (code !== null) return REJECTED_REQUEST_TRPC_ERROR_CODES.has(code);

  // A reply that is not a tRPC error at all (a gateway's own JSON body) has no code, but the client keeps the
  // response it came with, and its status says the same thing. An edge rate-limit block is a 429 with a body
  // that is not JSON: transient by status, and with no response for tRPC to read, so it takes its three attempts.
  const status = getHttpStatus(error);
  return status !== null && status >= 400 && status < 500 && !TRANSIENT_CLIENT_ERROR_STATUSES.has(status);
}

/** The HTTP status of the response a tRPC client error came from, when the client kept it. */
function getHttpStatus(error: unknown): number | null {
  if (!(error instanceof TRPCClientError)) return null;
  const response: unknown = error.meta?.response;
  if (typeof response !== "object" || response === null || !("status" in response)) return null;
  return typeof response.status === "number" ? response.status : null;
}

/** Decode the untyped data on a runtime tRPC error before inspecting its code. */
export function getTrpcErrorCode(error: unknown): string | null {
  if (!(error instanceof TRPCClientError)) return null;
  const data: unknown = error.data;
  if (typeof data !== "object" || data === null || !("code" in data)) return null;
  return typeof data.code === "string" ? data.code : null;
}

/**
 * The reason code a server refusal carries (`data.reasonCode`, set by the API's error factories),
 * for a caller that reacts to one specific refusal. Match on this, never on the message text.
 */
export function getTrpcErrorReasonCode(error: unknown): string | null {
  if (!(error instanceof TRPCClientError)) return null;
  const data: unknown = error.data;
  if (typeof data !== "object" || data === null || !("reasonCode" in data)) return null;
  return typeof data.reasonCode === "string" ? data.reasonCode : null;
}
